/**
 * Variant promo
 *
 * Reveals a notice while one of a configured set of option values is selected,
 * and optionally moves itself directly beneath the option it watches. Rendered
 * by blocks/variant-promo.liquid.
 *
 * Selection is read from the pickers rather than from a resolved variant: the
 * offer is tied to one option value (a high chair), so the notice should appear
 * as soon as that chair is picked, before a colour narrows it down to a single
 * variant.
 *
 * Add ?variant-promo-debug to the URL to log what the block can see.
 */

const PICKER_SELECTOR = 'custom-variant-picker, variant-picker';
const HIGH_CHAIR_SEARCH = '.custom-high-chair__search';
const DEBUG = new URLSearchParams(location.search).has('variant-promo-debug');

/**
 * The option a picker control belongs to. The custom picker labels its
 * fieldsets, the stock picker encodes the name in `options[Colour]`.
 */
function optionNameFor(element) {
  const labelled = element.closest('[data-option-name]');
  if (labelled) return labelled.dataset.optionName || '';

  const match = /^options\[(.*)\]$/.exec(element.name || '');
  return match ? match[1] : '';
}

const normalize = (value) => (value || '').trim().toLowerCase();

/**
 * Everything the pickers currently report as chosen, as {value, option} pairs.
 *
 * The high chair group is read from its search field rather than its radios:
 * the radios are hidden plumbing, one can be checked on page load before the
 * shopper has chosen anything, and typing over the field leaves the previous
 * one checked. The field is what the picker itself trusts to enable
 * add-to-cart, so it is the honest signal here too.
 */
function selectedOptions() {
  const found = [];

  for (const search of document.querySelectorAll(HIGH_CHAIR_SEARCH)) {
    found.push({ value: search.value, option: optionNameFor(search) });
  }

  for (const picker of document.querySelectorAll(PICKER_SELECTOR)) {
    for (const radio of picker.querySelectorAll('input[type="radio"]:checked')) {
      if (radio.closest('.custom-high-chair')) continue;
      found.push({ value: radio.value, option: optionNameFor(radio) });
    }

    for (const select of picker.querySelectorAll('select')) {
      found.push({ value: select.value, option: optionNameFor(select) });
    }
  }

  return found;
}

class VariantPromo extends HTMLElement {
  #ready = false;
  #placed = false;

  connectedCallback() {
    this.#init();

    // Re-added on every connect because relocating the element disconnects it.
    // addEventListener de-duplicates identical listeners, so this is safe.
    for (const type of ['change', 'input', 'variant:update', 'catchy:variant-change']) {
      document.addEventListener(type, this.#sync);
    }
    // The high chair list commits a choice on click; the picker updates the
    // field and radio in the same handler, so re-check once that has run.
    document.addEventListener('click', this.#syncSoon);

    this.#place();
    this.#sync();
  }

  disconnectedCallback() {
    for (const type of ['change', 'input', 'variant:update', 'catchy:variant-change']) {
      document.removeEventListener(type, this.#sync);
    }
    document.removeEventListener('click', this.#syncSoon);
  }

  #init() {
    if (this.#ready) return;
    this.#ready = true;

    this.triggers = (this.dataset.triggerValues || '').split('||').map(normalize).filter(Boolean);
    this.optionName = normalize(this.dataset.optionName);
  }

  /** True when one of the watched option values is currently selected. */
  #isActive() {
    if (!this.triggers.length) return false;

    const active = selectedOptions().some(
      ({ value, option }) =>
        this.triggers.includes(normalize(value)) &&
        (!this.optionName || normalize(option) === this.optionName)
    );

    if (DEBUG) {
      console.log('[variant-promo]', {
        active,
        triggers: this.triggers,
        optionName: this.optionName || '(any)',
        selected: selectedOptions(),
        pickersFound: document.querySelectorAll(PICKER_SELECTOR).length,
      });
    }

    return active;
  }

  #sync = () => {
    this.hidden = !this.#isActive();
  };

  #syncSoon = () => {
    requestAnimationFrame(this.#sync);
  };

  /**
   * Move the notice beneath the option it watches, so it reads as part of that
   * dropdown. Runs once — `#placed` is set before the move because inserting
   * the element disconnects and reconnects it, re-entering connectedCallback.
   */
  #place() {
    if (this.#placed || this.dataset.placement !== 'option') return;

    const target = this.#triggerFieldset();
    if (!target || target.nextElementSibling === this) {
      this.#placed = true;
      return;
    }

    this.#placed = true;
    target.after(this);
  }

  /** The fieldset holding the watched option, by name if given, else by value. */
  #triggerFieldset() {
    for (const fieldset of document.querySelectorAll('[data-option-name]')) {
      if (this.optionName) {
        if (normalize(fieldset.dataset.optionName) === this.optionName) return fieldset;
        continue;
      }

      const values = [
        ...fieldset.querySelectorAll('input[type="radio"]'),
        ...fieldset.querySelectorAll('option'),
      ];

      if (values.some((node) => this.triggers.includes(normalize(node.value)))) return fieldset;
    }

    return null;
  }
}

if (!customElements.get('variant-promo')) {
  customElements.define('variant-promo', VariantPromo);
}
